import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { isVersionOutdated } from '../utils/semver'

const DISMISS_KEY = 'press_update_dismissed_at'
const DISMISS_COOLDOWN_MS = 24 * 60 * 60 * 1000 // 24 hours

const STORE_URLS = {
  ios: 'https://apps.apple.com/us/app/press2dn/id6759986390',
  android: 'https://play.google.com/apps/testing/com.press2dn.app',
}

function wasRecentlyDismissed() {
  try {
    const ts = localStorage.getItem(DISMISS_KEY)
    if (!ts) return false
    return Date.now() - Number(ts) < DISMISS_COOLDOWN_MS
  } catch {
    return false
  }
}

export function useVersionCheck() {
  const [updateRequired, setUpdateRequired] = useState(false)
  const [updateRecommended, setUpdateRecommended] = useState(false)
  const [storeUrl, setStoreUrl] = useState(null)

  useEffect(() => {
    const isNative = typeof window !== 'undefined' &&
      window.Capacitor?.isNativePlatform?.()
    if (!isNative) return

    let cancelled = false

    ;(async () => {
      try {
        const { App } = await import('@capacitor/app')
        const info = await App.getInfo()
        const currentVersion = info.version

        const { data, error } = await supabase
          .from('app_config')
          .select('min_ios_version, min_android_version, recommended_ios_version, recommended_android_version')
          .single()

        if (error || !data || cancelled) return // Fail open

        const isIOS = window.Capacitor.getPlatform() === 'ios'
        const minVersion = isIOS ? data.min_ios_version : data.min_android_version
        const recVersion = isIOS ? data.recommended_ios_version : data.recommended_android_version
        const url = isIOS ? STORE_URLS.ios : STORE_URLS.android

        setStoreUrl(url)

        if (isVersionOutdated(currentVersion, minVersion)) {
          setUpdateRequired(true)
        } else if (isVersionOutdated(currentVersion, recVersion) && !wasRecentlyDismissed()) {
          setUpdateRecommended(true)
        }
      } catch {
        // Fail open — don't block the app
      }
    })()

    return () => { cancelled = true }
  }, [])

  const dismissRecommendation = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()))
    } catch { /* ignore */ }
    setUpdateRecommended(false)
  }

  return { updateRequired, updateRecommended, storeUrl, dismissRecommendation }
}
